import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AppLayout } from "../components/layout/AppLayout";
import { Card } from "../components/ui/Card";
import { RadarChart } from "../components/ui/RadarChart";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../lib/api";
import type { CompanyProfile } from "../types";

type JobItem = { id: string; title: string; status: string; created_at: string };
type InterviewItem = { id: string; title: string; status: string; candidate_name: string; created_at: string };

export default function CompanyDashboard() {
  const { user } = useAuth();
  const [company, setCompany] = useState<CompanyProfile | null>(null);
  const [jobs, setJobs] = useState<JobItem[]>([]);
  const [interviews, setInterviews] = useState<InterviewItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const [c, j, iv] = await Promise.all([
          api.get<{ company: CompanyProfile | null }>("/api/company/me"),
          api.get<{ items: JobItem[] }>("/api/company/jobs"),
          api.get<{ items: InterviewItem[] }>("/api/company/interviews"),
        ]);
        setCompany(c.company);
        setJobs(j.items);
        setInterviews(iv.items);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load data.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <AppLayout>{null}</AppLayout>;

  return (
    <AppLayout>
      <h1 className="text-lg font-bold text-navy">Good morning, {user?.name?.split(" ")[0]} 👋</h1>
      <p className="mt-1 text-sm text-slate-500">Discover talents that perfectly fit your team culture.</p>

      {error && <Card className="mt-3 border border-coral/30 bg-coral/5 text-xs text-coral">{error}</Card>}

      {!company && (
        <Card className="mt-4 border border-dashed border-slate-200 text-center">
          <p className="text-sm font-semibold text-navy">No Team Culture Profile Yet</p>
          <p className="mt-1 text-xs text-slate-400">Create a team culture profile to start matching with matching candidates.</p>
          <Link to="/company/culture" className="mt-3 inline-block rounded-full bg-navy px-4 py-2 text-xs font-semibold text-white">
            Set Team Culture
          </Link>
        </Card>
      )}

      {company && (
        <>
          <div className="mt-4 grid grid-cols-2 gap-3">
            <Card className="border border-slate-100 py-3 text-center">
              <p className="text-lg font-bold text-navy">{jobs.length}</p>
              <p className="text-[11px] text-slate-400">Job Postings</p>
            </Card>
            <Card className="border border-slate-100 py-3 text-center">
              <p className="text-lg font-bold text-navy">{interviews.length}</p>
              <p className="text-[11px] text-slate-400">AI Interviews</p>
            </Card>
          </div>

          <Card className="mt-3 border border-slate-100">
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold text-navy">{company.name}</p>
              <Link to="/company/culture" className="text-xs font-medium text-teal-dark">
                Edit →
              </Link>
            </div>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {company.cultureValues.map((v) => (
                <span key={v} className="rounded-full bg-slate-50 px-2 py-1 text-[11px] text-slate-500">
                  {v}
                </span>
              ))}
            </div>
            <div className="mt-2 flex justify-center">
              <RadarChart traits={company.cultureTraits} size={220} />
            </div>
          </Card>

          <Link to="/company/talent" className="mt-4 block">
            <Card className="border border-slate-100 bg-gradient-to-br from-navy to-navy-light text-white">
              <p className="text-sm font-bold">Team-fit Matchmaker</p>
              <p className="mt-1 text-xs text-slate-200">Find candidates who fit your team culture now.</p>
            </Card>
          </Link>

          <div className="mt-5">
            <p className="text-sm font-semibold text-navy">Recent AI Interviews</p>
            <div className="mt-2 space-y-2">
              {interviews.length === 0 && (
                <Card className="border border-dashed border-slate-200 text-center text-xs text-slate-400">
                  No interviews conducted yet.
                </Card>
              )}
              {interviews.slice(0, 5).map((iv) => (
                <Card key={iv.id} className="flex items-center justify-between border border-slate-100">
                  <div>
                    <p className="text-sm font-medium text-navy">{iv.candidate_name}</p>
                    <p className="text-[11px] text-slate-400">{iv.title}</p>
                  </div>
                  <span className="rounded-full bg-slate-50 px-2 py-1 text-[10px] text-slate-500">{iv.status}</span>
                </Card>
              ))}
            </div>
          </div>
        </>
      )}
    </AppLayout>
  );
}
